import {Check, Copy} from 'lucide-react';
import {useIntl} from 'react-intl';
import {insightsMessages as m} from './insightsMessages';
import {iconButton, lucide, secondaryButton} from './insightsUi';
import {useCopyFeedback} from './useCopyFeedback';

interface CopyButtonProps {
    label: string;
    getText: () => string;
    onCopied?: () => void;
    disabled?: boolean;
    variant?: 'icon' | 'text';
}

export const CopyButton = ({label, getText, onCopied, disabled, variant = 'icon'}: CopyButtonProps) => {
    const intl = useIntl();
    const {copied, failed, copy} = useCopyFeedback();

    const onClick = async () => {
        if (await copy(getText())) onCopied?.();
    };

    const icon = (
        <span className="relative inline-flex size-[18px] items-center justify-center">
            <Copy size={18} aria-hidden="true" className={`${lucide} absolute transition-[transform,opacity] duration-200 ease-out-expo motion-reduce:transition-none ${copied ? 'scale-50 opacity-0' : 'scale-100 opacity-100'}`}/>
            <Check size={18} aria-hidden="true" className={`${lucide} absolute text-success-ink transition-[transform,opacity] duration-200 ease-out-expo motion-reduce:transition-none ${copied ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}/>
        </span>
    );

    return (
        <span className="relative inline-flex">
            <button
                type="button"
                aria-label={variant === 'icon' ? label : undefined}
                title={variant === 'icon' ? label : undefined}
                disabled={disabled}
                onClick={onClick}
                className={variant === 'icon' ? iconButton : secondaryButton}
            >
                {icon}
                {variant === 'text' && <span>{copied ? intl.formatMessage(m.copied) : label}</span>}
            </button>
            <span aria-live="polite" className="sr-only">{copied ? intl.formatMessage(m.copied) : failed ? intl.formatMessage(m.copyFailed) : ''}</span>
            {failed && (
                <span role="alert" className="absolute right-0 top-full z-10 mt-1 w-max max-w-[240px] rounded-chip bg-inverse-surface px-2.5 py-1.5 text-footnote text-white shadow-menu">
                    {intl.formatMessage(m.copyFailed)}
                </span>
            )}
        </span>
    );
};
